import React from 'react';
import { Platform, ScrollView, Text, TouchableOpacity, View } from 'react-native';

import { CalendarDateInfo, LivesCalendarItem } from '../../hooks/useLivesCalendar';
import { fontWeights } from '../../styles/typographyTokens';

type LivesCalendarDateStripProps = {
  accentColor: string;
  dateCellGap: number;
  dateCellW: number;
  dateRange: CalendarDateInfo[];
  dayNames: string[];
  getDayMarkers: (dateISO: string) => {
    dayEvents: LivesCalendarItem[];
    hasEvents: boolean;
    hasReminder: boolean;
  };
  isDark: boolean;
  isMobile: boolean;
  markerMode: 'simple' | 'endedAware';
  month: number;
  onScroll: (event: any) => void;
  onLayout?: (event: any) => void;
  scrollRef: React.RefObject<ScrollView | null>;
  selectedDateStr: string;
  setSelectedDateStr: (date: string) => void;
  setViewedDate: (date: Date) => void;
  themeColors: {
    textPrimary: string;
  };
  todayISO: string;
  year: number;
};

export const LivesCalendarDateStrip: React.FC<LivesCalendarDateStripProps> = ({
  accentColor,
  dateCellGap,
  dateCellW,
  dateRange,
  dayNames,
  getDayMarkers,
  isDark,
  isMobile,
  markerMode,
  month,
  onScroll,
  onLayout,
  scrollRef,
  selectedDateStr,
  setSelectedDateStr,
  setViewedDate,
  themeColors,
  todayISO,
  year,
}) => (
  <View style={{ borderBottomWidth: 1, borderBottomColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        paddingHorizontal: isMobile ? 12 : 24,
        paddingVertical: 24,
        flexDirection: 'row',
        gap: dateCellGap,
      }}
      onScroll={onScroll}
      onLayout={onLayout}
      scrollEventThrottle={100}
    >
      {dateRange.map(dateInfo => {
        const { year: dYear, month: dMonth, day, dateISO } = dateInfo;
        const dateObj = new Date(dYear, dMonth, day);
        const dayOfWeek = dateObj.getDay();
        const dayName = dayNames[dayOfWeek];
        const isSelected = dateISO === selectedDateStr;
        const isToday = dateISO === todayISO;
        const isOtherMonth = dMonth !== month || dYear !== year;
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const { dayEvents, hasEvents, hasReminder } = getDayMarkers(dateISO);

        const numColor = isSelected ? (isDark ? '#FFF7F1' : themeColors.textPrimary)
          : isToday ? accentColor
            : isOtherMonth ? (isDark ? '#555' : '#BBB')
              : themeColors.textPrimary;
        const nameColor = isSelected ? accentColor
          : isWeekend ? accentColor
            : isOtherMonth ? (isDark ? '#444' : '#CCC')
              : (isDark ? '#888' : '#888');

        let allEnded = false;
        if (markerMode === 'endedAware' && hasEvents) {
          if (dateISO < todayISO) {
            allEnded = true;
          } else if (dateISO === todayISO && dayEvents.length > 0) {
            const now = Date.now();
            allEnded = dayEvents.every(event => {
              const start = new Date(event.startDateTimeUtc).getTime();
              const dur = (event.durationMinutes || 60) * 60 * 1000;
              return now > start + dur;
            });
          }
        }

        const marker = (() => {
          if (markerMode === 'simple') {
            if (hasReminder) {
              return <View style={{ width: 20, height: 3, borderRadius: 1.5, marginTop: 4, backgroundColor: accentColor }} />;
            }
            if (hasEvents && !isSelected) {
              return <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', marginTop: 4 }} />;
            }
            return <View style={{ width: 4, height: 4, marginTop: 4 }} />;
          }

          if (hasReminder && !allEnded) {
            return <View style={{ width: 24, height: 4, borderRadius: 2, marginTop: 4, backgroundColor: accentColor }} />;
          }
          if (hasEvents && allEnded) {
            return <View style={{ width: 24, height: 4, borderRadius: 2, marginTop: 4, backgroundColor: isSelected ? 'rgba(242,106,54,0.28)' : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)') }} />;
          }
          if (hasEvents) {
            return <View style={{ width: 24, height: 4, borderRadius: 2, marginTop: 4, backgroundColor: isSelected ? 'rgba(242,106,54,0.45)' : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)') }} />;
          }
          return <View style={{ width: 24, height: 4, borderRadius: 2, marginTop: 4 }} />;
        })();

        return (
          <TouchableOpacity
            key={dateISO}
            style={[
              {
                width: dateCellW,
                height: isMobile ? 62 : 78,
                borderRadius: isMobile ? 16 : 20,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isSelected
                  ? (isDark ? 'rgba(242,106,54,0.12)' : 'rgba(255,247,241,0.82)')
                  : 'transparent',
                borderWidth: isSelected ? 1.5 : 0,
                borderColor: isSelected ? accentColor : 'transparent',
                ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' } as any }),
              },
              isSelected && {
                shadowColor: 'rgba(233, 130, 69, 1)',
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.14,
                shadowRadius: 14,
                elevation: 2,
              },
            ]}
            onPress={() => {
              setSelectedDateStr(dateISO);
              if (isOtherMonth) {
                setViewedDate(new Date(dYear, dMonth, 1));
              }
            }}
            activeOpacity={0.8}
          >
            <Text style={{ color: nameColor, fontSize: isMobile ? 10 : 13, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 1, marginBottom: isMobile ? 2 : 4 }}>
              {dayName}
            </Text>
            <View style={{ width: 32, height: 32, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: numColor, fontSize: isMobile ? 16 : 22, fontWeight: fontWeights.bold }}>
                {day}
              </Text>
            </View>
            {marker}
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  </View>
);
